import React, { useState, useEffect, useMemo } from 'react';
import './EmployeeInfoModal.css';
import { API_ENDPOINTS, getAuthHeaders } from '../../config/api';

// Métiers IDCC 1619 (cabinets dentaires) — mapping vers niveau/échelon/coeff
const DENTAL_1619_ROLES = [
  { value: 'assistante_qualifiee', label: 'Assistante dentaire qualifiée', title: 'Assistante dentaire qualifiée', level: 'Niveau II', echelon: 'Échelon 3', coeff: 240 },
  { value: 'assistante_debutante', label: 'Assistante dentaire débutante', title: 'Assistante dentaire débutante', level: 'Niveau I', echelon: 'Échelon 1', coeff: 200 },
  { value: 'secretaire_medicale', label: 'Secrétaire médicale dentaire', title: 'Secrétaire médicale dentaire', level: 'Niveau I', echelon: 'Échelon 2', coeff: 210 },
  { value: 'responsable_sterilisation', label: 'Responsable de stérilisation', title: 'Responsable de stérilisation', level: 'Niveau II', echelon: 'Échelon 2', coeff: 230 },
  { value: 'hygieniste_dentaire', label: 'Hygiéniste dentaire (si applicable)', title: 'Hygiéniste dentaire', level: 'Niveau II', echelon: 'Échelon 4', coeff: 250 },
  { value: 'autre', label: 'Autre…' }
];

/**
 * Composant EmployeeInfoModal - Modal pour saisir/éditer les informations d'un employé
 * S'ouvre au clic sur le statut "Informations requises"
 */
export function EmployeeInfoModal({ 
  isOpen, 
  onClose, 
  employee = null, 
  operatorId = null,
  cabinet = null,
  onSaved = null 
}) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({});

  // Valeurs par défaut
  const defaultValues = useMemo(() => ({
    // Identité
    first_name: '',
    last_name: '',
    birth_date: '',
    birth_place: '',
    nir: '',
    
    // Identité - suite (nouveaux champs)
    gender: '',
    email: '',
    phone: '',
    nationality: 'FR',
    marital_status: '',
    children_count: 0,
    
    // Coordonnées
    address_l1: '',
    address_l2: '',
    postal_code: '',
    city: '',
    country: 'FR',
    
    // Ancienneté & Lieu
    seniority_date: '',
    workplace_location: '',
    
    // Contrat
    contract_type: 'CDI',
    start_date: '',
    end_date: '',
    working_time_pct: 100,
    weekly_hours: 35,
    school_hours_per_week: '',
    school_days: [],
    
    // Classification & Convention
    job_title_select: '', // pour IDCC 1619 : valeur du select métier
    job_title: '',
    position_code: '',
    classification_level: '',
    classification_echelon: '',
    classification_coefficient: '',
    status_cadre: 'NON_CADRE',
    
    // Rémunération
    salary_type: 'MENSUEL',
    salary_gross_base: '',
    withholding_code: '0',
    withholding_rate: '0',
    
    // Titres-restaurant
    meal_vouchers_enabled: false,
    meal_voucher_value: '',
    meal_voucher_employer_share_percent: '',
    meal_voucher_days_per_month: '',
    
    // Transport
    transport_enabled: false,
    transport_monthly_cost: '',
    transport_employer_share_percent: '',
    
    // Avantages en nature
    benefit_vehicle_amount: '',
    benefit_housing_amount: '',
    benefit_meals_amount: '',
    
    // Organismes
    mutuelle_status: 'with_mutuelle', // 'with_mutuelle' ou 'no_mutuelle'
    mutuelle_selected: '',
    mutuelle_opt_out_reason: '',
    mutuelle_opt_out_doc_url: '',
    prevoyance_selected: '',
    retraite_institution: '',
    
    // Paiement
    employee_iban: '',
    employee_bic: '',
    payment_method: 'VIREMENT',
    
    // Apprentissage - complément
    school_name: '',
    school_city: '',
    
    // Autres
    note: '',
    cabinet_id: null
  }), []);

  // Reset form à l'ouverture
  useEffect(() => {
    if (isOpen) {
      if (employee) {
        // Mode édition - pré-remplir avec les données existantes
        // Convertir l'ancien système mutuelle_opt_out vers mutuelle_status
        const mutuelleStatus = employee.mutuelle_opt_out ? 'no_mutuelle' : 'with_mutuelle';
        
        setFormData({
          ...defaultValues,
          ...employee,
          nir: employee.nir_last4 ? `****${employee.nir_last4}` : '', // Masquer le NIR complet
          mutuelle_status: mutuelleStatus,
          school_days: employee.school_days || [] // Assurer que c'est un tableau
        });
      } else {
        // Mode création
        setFormData(defaultValues);
      }
      setErrors({});
    }
  }, [isOpen, employee, defaultValues]);

  // Gestion des changements de champs
  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Comportements UX spécifiques
      
      // Si on désactive les titres-restaurant, vider les champs associés
      if (field === 'meal_vouchers_enabled' && !value) {
        newData.meal_voucher_value = '';
        newData.meal_voucher_employer_share_percent = '';
        newData.meal_voucher_days_per_month = '';
      }
      
      // Si on désactive le transport, vider les champs associés
      if (field === 'transport_enabled' && !value) {
        newData.transport_monthly_cost = '';
        newData.transport_employer_share_percent = '';
      }
      
      // Si on change de type de contrat depuis APPRENTI, réinitialiser les champs apprentis
      if (field === 'contract_type' && prev.contract_type === 'APPRENTI' && value !== 'APPRENTI') {
        newData.school_hours_per_week = '';
        newData.school_days = [];
        newData.school_name = '';
        newData.school_city = '';
      }
      
      // Si on passe à no_mutuelle, vider mutuelle_selected
      if (field === 'mutuelle_status' && value === 'no_mutuelle') {
        newData.mutuelle_selected = '';
      }
      
      // Sélection d'un métier 1619
      if (field === 'job_title_select') {
        const opt = DENTAL_1619_ROLES.find(o => o.value === value);
        if (opt && opt.value !== 'autre') {
          newData.job_title = opt.title;
          newData.classification_level = opt.level;
          newData.classification_echelon = opt.echelon;
          newData.classification_coefficient = opt.coeff;
        } else {
          // "Autre..." : on vide pour laisser saisir librement
          newData.job_title = '';
          newData.classification_level = '';
          newData.classification_echelon = '';
          newData.classification_coefficient = '';
        }
      }
      
      return newData;
    });
    
    // Clear error si l'utilisateur corrige
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validation d'un champ
  const validateField = (field, value) => {
    switch (field) {
      case 'first_name':
      case 'last_name':
        return !value?.trim() ? 'Ce champ est obligatoire' : null;
      
      case 'birth_date':
        if (!value) return 'La date de naissance est obligatoire';
        const birthDate = new Date(value);
        const today = new Date();
        if (birthDate >= today) return 'La date doit être dans le passé';
        return null;
      
      case 'address_l1':
      case 'city':
        return !value?.trim() ? 'Ce champ est obligatoire' : null;
      
      case 'postal_code': {
        const v = (value || '').trim();
        if (!v) return 'Le code postal est obligatoire';
        if (!/^\d{5}$/.test(v)) return 'Code postal invalide (5 chiffres)';
        return null;
      }
      
      case 'start_date':
        if (!value) return 'La date d\'entrée est obligatoire';
        return null;
      
      case 'salary_gross_base':
        if (!value) return 'Le salaire de base est obligatoire';
        const salary = parseFloat(value);
        if (isNaN(salary) || salary <= 0) return 'Salaire invalide';
        return null;
      
      case 'withholding_rate':
        if (value && (value < 0 || value > 1)) {
          return 'Le taux doit être entre 0 et 1 (ex: 0.10 pour 10%)';
        }
        return null;
      
      case 'employee_iban':
        if (value && !/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(value.replace(/\s/g, ''))) {
          return 'Format IBAN invalide';
        }
        return null;
      
      case 'employee_bic':
        if (value && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value)) {
          return 'Format BIC invalide';
        }
        return null;
      
      // Nouveaux champs - Identité/Contact
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Format email invalide';
        }
        return null;
      
      case 'phone':
        if (value && !/^[0-9\s\+\(\)\-]{6,}$/.test(value)) {
          return 'Numéro de téléphone invalide (min 6 caractères)';
        }
        return null;
      
      case 'nationality':
        if (value && value.length !== 2) {
          return 'Code pays invalide (2 lettres ISO)';
        }
        return null;
      
      case 'marital_status':
        const validStatuses = ['CELIBATAIRE', 'MARIE', 'PACS', 'DIVORCE', 'VEUF', ''];
        if (value && !validStatuses.includes(value)) {
          return 'Statut marital invalide';
        }
        return null;
      
      case 'children_count':
        if (value && (isNaN(value) || parseInt(value) < 0)) {
          return 'Nombre d\'enfants invalide';
        }
        return null;
      
      // Ancienneté
      case 'seniority_date':
        if (value && formData.start_date) {
          const seniorityDate = new Date(value);
          const startDate = new Date(formData.start_date);
          if (seniorityDate > startDate) {
            return 'La date d\'ancienneté doit être antérieure ou égale à la date d\'entrée';
          }
        }
        return null;
      
      // Classification
      case 'classification_coefficient':
        if (value !== '' && (isNaN(parseFloat(value)) || parseFloat(value) <= 0)) {
          return 'Coefficient invalide (doit être un nombre positif)';
        }
        return null;
      
      case 'status_cadre':
        if (value && !['CADRE', 'NON_CADRE'].includes(value)) {
          return 'Statut cadre invalide';
        }
        return null;
      
      // Titres-restaurant
      case 'meal_voucher_value':
        if (value !== '' && (isNaN(parseFloat(value)) || parseFloat(value) <= 0)) {
          return 'Valeur du titre-restaurant invalide (> 0)';
        }
        return null;
      
      case 'meal_voucher_employer_share_percent':
      case 'transport_employer_share_percent':
        if (value !== '' && (isNaN(parseFloat(value)) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
          return 'Pourcentage invalide (0-100%)';
        }
        return null;
      
      case 'meal_voucher_days_per_month':
        if (value !== '' && (isNaN(parseInt(value)) || parseInt(value) < 0 || parseInt(value) > 31)) {
          return 'Nombre de jours invalide (0-31)';
        }
        return null;
      
      // Transport
      case 'transport_monthly_cost':
        if (value !== '' && (isNaN(parseFloat(value)) || parseFloat(value) < 0)) {
          return 'Montant invalide (≥ 0)';
        }
        return null;
      
      // Avantages en nature
      case 'benefit_vehicle_amount':
      case 'benefit_housing_amount':
      case 'benefit_meals_amount':
        if (value !== '' && (isNaN(parseFloat(value)) || parseFloat(value) < 0)) {
          return 'Montant invalide (≥ 0)';
        }
        return null;
      
      default:
        return null;
    }
  };

  // Validation complète du formulaire
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Champs obligatoires
    const requiredFields = [
      'first_name', 'last_name', 'birth_date', 'address_l1', 
      'postal_code', 'city', 'start_date', 'salary_gross_base'
    ];

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    // Validation conditionnelle
    Object.keys(formData).forEach(field => {
      if (!requiredFields.includes(field)) {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
          isValid = false;
        }
      }
    });

    // Validation spécifique pour les apprentis
    if (formData.contract_type === 'APPRENTI') {
      if (!formData.end_date) {
        newErrors.end_date = 'La date de fin est obligatoire pour un apprenti';
        isValid = false;
      }
      if (!formData.school_hours_per_week || formData.school_hours_per_week <= 0) {
        newErrors.school_hours_per_week = 'Les heures école/semaine sont obligatoires pour un apprenti';
        isValid = false;
      }
      if (!formData.school_days || formData.school_days.length === 0) {
        newErrors.school_days = 'Au moins un jour d\'école doit être sélectionné pour un apprenti';
        isValid = false;
      }
      // Note : school_name et school_city sont optionnels (non requis pour la paie/DSN)
    }

    // Validation conditionnelle pour les titres-restaurant
    if (formData.meal_vouchers_enabled) {
      if (!formData.meal_voucher_value || formData.meal_voucher_value === '') {
        newErrors.meal_voucher_value = 'La valeur du titre-restaurant est requise';
        isValid = false;
      }
      if (formData.meal_voucher_employer_share_percent === '') {
        newErrors.meal_voucher_employer_share_percent = 'La part employeur est requise';
        isValid = false;
      }
      if (formData.meal_voucher_days_per_month === '') {
        newErrors.meal_voucher_days_per_month = 'Le nombre de jours est requis';
        isValid = false;
      }
    }

    // Validation conditionnelle pour le transport
    if (formData.transport_enabled) {
      if (formData.transport_monthly_cost === '') {
        newErrors.transport_monthly_cost = 'Le coût mensuel est requis';
        isValid = false;
      }
      if (formData.transport_employer_share_percent === '') {
        newErrors.transport_employer_share_percent = 'La part employeur est requise';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔧 Employee Modal - Submit started');

    if (!validateForm()) {
      console.log('❌ Employee Modal - Validation failed:', errors);
      return;
    }

    setLoading(true);
    console.log('🔧 Employee Modal - Form data:', formData);
    console.log('🔧 Employee Modal - Cabinet:', cabinet);
    console.log('🔧 Employee Modal - Cabinet ID:', cabinet?.id);

    try {
      // Hash du NIR si fourni
      let nirHash = null;
      let nirLast4 = null;
      
      if (formData.nir && formData.nir.trim()) {
        const cleanNir = formData.nir.replace(/\*/g, '').replace(/\s/g, '');
        
        // Hash SHA-256 du NIR
        const msgBuffer = new TextEncoder().encode(cleanNir);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        nirHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        nirLast4 = cleanNir.slice(-4);
      }
      
      // Préparer les données pour l'API
      const payload = {
        // Identité
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        birth_date: formData.birth_date,
        birth_place: formData.birth_place?.trim() || null,
        
        // NIR - hashé côté frontend
        nir_hash: nirHash,
        nir_last4: nirLast4,
        
        // Identité - suite (nouveaux champs)
        gender: formData.gender || null,
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        nationality: formData.nationality?.toUpperCase() || null,
        marital_status: formData.marital_status || null,
        children_count: Number.isFinite(parseInt(formData.children_count)) ? parseInt(formData.children_count) : 0,
        
        // Coordonnées
        address_l1: formData.address_l1.trim(),
        address_l2: formData.address_l2?.trim() || null,
        postal_code: formData.postal_code.trim(),
        city: formData.city.trim(),
        country: formData.country,
        
        // Ancienneté & Lieu
        seniority_date: formData.seniority_date || null,
        workplace_location: formData.workplace_location?.trim() || null,
        
        // Contrat
        contract_type: formData.contract_type,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        working_time_pct: formData.working_time_pct ? parseInt(formData.working_time_pct) : 100,
        weekly_hours: formData.weekly_hours ? parseInt(formData.weekly_hours) : null,
        
        // Classification & Convention
        job_title: formData.job_title?.trim() || null,
        position_code: formData.position_code?.trim() || null,
        classification_level: formData.classification_level?.trim() || null,
        classification_echelon: formData.classification_echelon?.trim() || null,
        classification_coefficient: (formData.classification_coefficient !== '' && !isNaN(parseFloat(formData.classification_coefficient)))
          ? parseFloat(formData.classification_coefficient)
          : null,
        status_cadre: formData.status_cadre || 'NON_CADRE',
        
        // Rémunération
        salary_type: formData.salary_type,
        salary_gross_base: parseFloat(formData.salary_gross_base),
        withholding_code: formData.withholding_code || null,
        withholding_rate: formData.withholding_rate ? parseFloat(formData.withholding_rate) : null,
        
        // Titres-restaurant
        meal_vouchers_enabled: !!formData.meal_vouchers_enabled,
        meal_voucher_value: formData.meal_vouchers_enabled && formData.meal_voucher_value !== '' ? parseFloat(formData.meal_voucher_value) : null,
        meal_voucher_employer_share_percent: formData.meal_vouchers_enabled && formData.meal_voucher_employer_share_percent !== '' ? parseFloat(formData.meal_voucher_employer_share_percent) : null,
        meal_voucher_days_per_month: formData.meal_vouchers_enabled && formData.meal_voucher_days_per_month !== '' ? parseInt(formData.meal_voucher_days_per_month) : null,
        
        // Transport
        transport_enabled: !!formData.transport_enabled,
        transport_monthly_cost: formData.transport_enabled && formData.transport_monthly_cost !== '' ? parseFloat(formData.transport_monthly_cost) : null,
        transport_employer_share_percent: formData.transport_enabled && formData.transport_employer_share_percent !== '' ? parseFloat(formData.transport_employer_share_percent) : null,
        
        // Avantages en nature
        benefit_vehicle_amount: formData.benefit_vehicle_amount !== '' ? parseFloat(formData.benefit_vehicle_amount) : null,
        benefit_housing_amount: formData.benefit_housing_amount !== '' ? parseFloat(formData.benefit_housing_amount) : null,
        benefit_meals_amount: formData.benefit_meals_amount !== '' ? parseFloat(formData.benefit_meals_amount) : null,
        
        // Organismes - convertir mutuelle_status en mutuelle_opt_out pour le backend
        mutuelle_selected: formData.mutuelle_status === 'with_mutuelle' ? (formData.mutuelle_selected || null) : null,
        mutuelle_opt_out: formData.mutuelle_status === 'no_mutuelle',
        mutuelle_opt_out_reason: formData.mutuelle_status === 'no_mutuelle' ? (formData.mutuelle_opt_out_reason?.trim() || null) : null,
        prevoyance_selected: formData.prevoyance_selected || null,
        retraite_institution: formData.retraite_institution || null,
        
        // Paiement
        employee_iban: formData.employee_iban?.replace(/\s/g, '').toUpperCase() || null,
        employee_bic: formData.employee_bic?.toUpperCase() || null,
        payment_method: formData.payment_method,
        
        // Autres
        note: formData.note?.trim() || null,
        cabinet_id: cabinet?.id || null, // Utiliser l'ID du cabinet
        operator_id: operatorId // Associer l'employé à l'opérateur
      };

      console.log('🔧 Employee Modal - Payload:', payload);

      const method = employee ? 'PUT' : 'POST';
      const url = employee 
        ? API_ENDPOINTS.EMPLOYEE_DETAIL(employee.id)
        : API_ENDPOINTS.EMPLOYEES_LIST();

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      console.log('🔧 Employee Modal - Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Employee Modal - API Error:', errorData);
        throw new Error(errorData.error?.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Employee Modal - Save successful:', result);

      // Si c'est un apprenti, enregistrer aussi les infos école
      if (formData.contract_type === 'APPRENTI' && operatorId) {
        console.log('🏫 Enregistrement des informations école...');
        
        // Validation des données école
        if (!formData.school_hours_per_week || formData.school_days.length === 0) {
          throw new Error('Pour un apprenti, les heures école/semaine et les jours d\'école sont obligatoires');
        }

        // Récupérer l'employee_id depuis le résultat de la sauvegarde
        // En mode édition, on utilise l'ID de l'employé existant
        // En mode création, on utilise l'ID retourné par l'API
        let employeeId = employee?.id || result.id || result.data?.id || result.employee?.id;
        
        console.log('🏫 Employee ID détecté:', employeeId);
        console.log('🏫 Structure de result:', Object.keys(result));
        
        if (!employeeId) {
          console.error('❌ Impossible de trouver employee_id dans:', result);
          throw new Error('Impossible de récupérer l\'ID de l\'employé après sauvegarde');
        }

        const schoolPayload = {
          operator_id: operatorId,
          employee_id: employeeId,
          weekly_hours: parseInt(formData.school_hours_per_week),
          school_days: formData.school_days,
          school_start_time: '08:30:00', // Heure par défaut
          ...(formData.school_name?.trim() ? { school_name: formData.school_name.trim() } : {}),
          ...(formData.school_city?.trim() ? { school_city: formData.school_city.trim() } : {})
        };

        console.log('🏫 School payload:', schoolPayload);

        // Vérifier si une config école existe déjà
        const checkSchoolResponse = await fetch(API_ENDPOINTS.SCHOOL_DETAIL(operatorId), {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include'
        });

        let schoolResponse;
        if (checkSchoolResponse.ok) {
          // Config existe déjà, on fait un PUT
          console.log('🏫 Mise à jour de la configuration école existante');
          schoolResponse = await fetch(API_ENDPOINTS.SCHOOL_DETAIL(operatorId), {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(schoolPayload)
          });
        } else {
          // Nouvelle config, on fait un POST
          console.log('🏫 Création d\'une nouvelle configuration école');
          schoolResponse = await fetch(API_ENDPOINTS.SCHOOL_LIST(), {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(schoolPayload)
          });
        }

        if (!schoolResponse.ok) {
          const errorData = await schoolResponse.json();
          console.error('❌ School API Error:', errorData);
          throw new Error(`Erreur lors de l'enregistrement des informations école: ${errorData.error || errorData.detail || 'Erreur inconnue'}`);
        }

        const schoolResult = await schoolResponse.json();
        console.log('✅ School info saved:', schoolResult);
      }

      // Toast de succès
      showToast('Employé enregistré avec succès', 'success');
      
      // Fermer le modal et notifier le parent
      onClose();
      if (onSaved) onSaved();

    } catch (error) {
      console.error('❌ Employee Modal - Save error:', error);
      showToast(error.message || 'Erreur lors de l\'enregistrement', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fonction toast simple
  const showToast = (message, type = 'success') => {
    // Simple toast - vous pouvez remplacer par votre système de toast
    alert(`${type === 'success' ? '✅' : '❌'} ${message}`);
  };

  // Vérifier si des organismes obligatoires manquent
  const hasMissingOrganisms = formData.mutuelle_status === 'with_mutuelle' && 
                             (!formData.mutuelle_selected || !formData.prevoyance_selected || !formData.retraite_institution);

  // Options pour les selects
  const contractTypes = [
    { value: 'CDI', label: 'CDI' },
    { value: 'CDD', label: 'CDD' },
    { value: 'APPRENTI', label: 'Apprenti' },
    { value: 'INTERIM', label: 'Intérim' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  const salaryTypes = [
    { value: 'MENSUEL', label: 'Mensuel' },
    { value: 'HORAIRE', label: 'Horaire' }
  ];

  const paymentMethods = [
    { value: 'VIREMENT', label: 'Virement' },
    { value: 'CHEQUE', label: 'Chèque' },
    { value: 'ESPECES', label: 'Espèces' }
  ];

  const mutuelleOptions = [
    { value: '', label: 'Sélectionner une mutuelle' },
    { value: 'harmonie', label: 'Harmonie Mutuelle' },
    { value: 'apgis', label: 'APGIS' },
    { value: 'malakoff', label: 'Malakoff Humanis' },
    { value: 'autre', label: 'Autre...' }
  ];

  const prevoyanceOptions = [
    { value: '', label: 'Sélectionner une prévoyance' },
    { value: 'apgis', label: 'APGIS' },
    { value: 'ag2r', label: 'AG2R La Mondiale' },
    { value: 'autre', label: 'Autre...' }
  ];

  const retraiteOptions = [
    { value: '', label: 'Sélectionner une caisse' },
    { value: 'malakoff', label: 'Malakoff Humanis (Agirc-Arrco)' },
    { value: 'ag2r', label: 'AG2R La Mondiale (Agirc-Arrco)' },
    { value: 'klesia', label: 'KLESIA (Agirc-Arrco)' },
    { value: 'autre', label: 'Autre...' }
  ];

  const schoolDayOptions = [
    { value: 'LUNDI', label: 'Lundi' },
    { value: 'MARDI', label: 'Mardi' },
    { value: 'MERCREDI', label: 'Mercredi' },
    { value: 'JEUDI', label: 'Jeudi' },
    { value: 'VENDREDI', label: 'Vendredi' }
  ];

  const mutuelleStatusOptions = [
    { value: 'with_mutuelle', label: 'Ajouter une mutuelle' },
    { value: 'no_mutuelle', label: 'Pas de mutuelle' }
  ];

  const genderOptions = [
    { value: '', label: 'Non spécifié' },
    { value: 'F', label: 'Féminin' },
    { value: 'M', label: 'Masculin' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  const maritalStatusOptions = [
    { value: '', label: 'Non spécifié' },
    { value: 'CELIBATAIRE', label: 'Célibataire' },
    { value: 'MARIE', label: 'Marié(e)' },
    { value: 'PACS', label: 'Pacsé(e)' },
    { value: 'DIVORCE', label: 'Divorcé(e)' },
    { value: 'VEUF', label: 'Veuf/Veuve' }
  ];

  const statusCadreOptions = [
    { value: 'NON_CADRE', label: 'Non-cadre' },
    { value: 'CADRE', label: 'Cadre' }
  ];

  if (!isOpen) return null;

  return (
    <div className="employee-modal-overlay">
      <div className="employee-modal-container">
        <div className="employee-modal-header">
          <h2 className="employee-modal-title">
            {employee ? 'Modifier les informations salarié' : 'Informations salarié – fiche de paie'}
          </h2>
          <button 
            className="employee-modal-close"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="employee-modal-form">
          <div className="employee-modal-body">
            
            {/* Alerte organismes manquants */}
            {hasMissingOrganisms && (
              <div className="employee-alert-warning">
                <div className="employee-alert-icon">⚠️</div>
                <div className="employee-alert-content">
                  <strong>Attention :</strong> Les organismes sociaux (mutuelle, prévoyance, retraite) sont vivement recommandés. 
                  En leur absence, certaines lignes de cotisations peuvent manquer.
                </div>
              </div>
            )}

            {/* Section 1: Identité */}
            <section className="employee-section">
              <h3 className="employee-section-title">Identité</h3>
              <div className="employee-form-grid">
                <div className="employee-form-field">
                  <label htmlFor="first_name" className="employee-field-label">
                    Prénom *
                  </label>
                  <input
                    id="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    className={`employee-input ${errors.first_name ? 'employee-input-error' : ''}`}
                    placeholder="Prénom"
                  />
                  {errors.first_name && (
                    <span className="employee-field-error">{errors.first_name}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="last_name" className="employee-field-label">
                    Nom *
                  </label>
                  <input
                    id="last_name"
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    className={`employee-input ${errors.last_name ? 'employee-input-error' : ''}`}
                    placeholder="Nom"
                  />
                  {errors.last_name && (
                    <span className="employee-field-error">{errors.last_name}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="birth_date" className="employee-field-label">
                    Date de naissance *
                  </label>
                  <input
                    id="birth_date"
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => handleChange('birth_date', e.target.value)}
                    className={`employee-input ${errors.birth_date ? 'employee-input-error' : ''}`}
                  />
                  {errors.birth_date && (
                    <span className="employee-field-error">{errors.birth_date}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="birth_place" className="employee-field-label">
                    Lieu de naissance
                  </label>
                  <input
                    id="birth_place"
                    type="text"
                    value={formData.birth_place}
                    onChange={(e) => handleChange('birth_place', e.target.value)}
                    className="employee-input"
                    placeholder="Ville"
                  />
                </div>

                <div className="employee-form-field employee-form-field-full">
                  <label htmlFor="nir" className="employee-field-label">
                    NIR (Numéro d'inscription au répertoire)
                  </label>
                  <input
                    id="nir"
                    type="text"
                    value={formData.nir}
                    onChange={(e) => handleChange('nir', e.target.value)}
                    className="employee-input"
                    placeholder="1234567890123"
                    maxLength="13"
                  />
                  <div className="employee-field-help">
                    Saisissez les 13 chiffres du NIR (sera hashé automatiquement, jamais stocké en clair)
                  </div>
                </div>
                {/* Nouveaux champs identité */}
                <div className="employee-form-field">
                  <label htmlFor="gender" className="employee-field-label">
                    Genre
                  </label>
                  <select
                    id="gender"
                    value={formData.gender}
                    onChange={(e) => handleChange('gender', e.target.value)}
                    className="employee-input"
                  >
                    {genderOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="employee-form-field">
                  <label htmlFor="marital_status" className="employee-field-label">
                    Situation familiale
                  </label>
                  <select
                    id="marital_status"
                    value={formData.marital_status}
                    onChange={(e) => handleChange('marital_status', e.target.value)}
                    className={`employee-input ${errors.marital_status ? 'employee-input-error' : ''}`}
                  >
                    {maritalStatusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {errors.marital_status && (
                    <span className="employee-field-error">{errors.marital_status}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="children_count" className="employee-field-label">
                    Nombre d'enfants
                  </label>
                  <input
                    id="children_count"
                    type="number"
                    min="0"
                    value={formData.children_count}
                    onChange={(e) => handleChange('children_count', e.target.value)}
                    className={`employee-input ${errors.children_count ? 'employee-input-error' : ''}`}
                    placeholder="0"
                  />
                  {errors.children_count && (
                    <span className="employee-field-error">{errors.children_count}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="nationality" className="employee-field-label">
                    Nationalité
                  </label>
                  <input
                    id="nationality"
                    type="text"
                    maxLength="2"
                    value={formData.nationality}
                    onChange={(e) => handleChange('nationality', e.target.value.toUpperCase())}
                    className={`employee-input ${errors.nationality ? 'employee-input-error' : ''}`}
                    placeholder="FR"
                  />
                  <span className="employee-subtle-hint">Code pays ISO (2 lettres)</span>
                  {errors.nationality && (
                    <span className="employee-field-error">{errors.nationality}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="email" className="employee-field-label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`employee-input ${errors.email ? 'employee-input-error' : ''}`}
                    placeholder="prenom.nom@exemple.fr"
                  />
                  {errors.email && (
                    <span className="employee-field-error">{errors.email}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="phone" className="employee-field-label">
                    Téléphone
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className={`employee-input ${errors.phone ? 'employee-input-error' : ''}`}
                    placeholder="+33 6 12 34 56 78"
                  />
                  {errors.phone && (
                    <span className="employee-field-error">{errors.phone}</span>
                  )}
                </div>
              </div>
            </section>

            {/* Section 2: Coordonnées */}
            <section className="employee-section">
              <h3 className="employee-section-title">Coordonnées</h3>
              <div className="employee-form-grid">
                <div className="employee-form-field employee-form-field-full">
                  <label htmlFor="address_l1" className="employee-field-label">
                    Adresse (ligne 1) *
                  </label>
                  <input
                    id="address_l1"
                    type="text"
                    value={formData.address_l1}
                    onChange={(e) => handleChange('address_l1', e.target.value)}
                    className={`employee-input ${errors.address_l1 ? 'employee-input-error' : ''}`}
                    placeholder="Numéro, rue"
                  />
                  {errors.address_l1 && (
                    <span className="employee-field-error">{errors.address_l1}</span>
                  )}
                </div>

                <div className="employee-form-field employee-form-field-full">
                  <label htmlFor="address_l2" className="employee-field-label">
                    Adresse (ligne 2)
                  </label>
                  <input
                    id="address_l2"
                    type="text"
                    value={formData.address_l2}
                    onChange={(e) => handleChange('address_l2', e.target.value)}
                    className="employee-input"
                    placeholder="Complément d'adresse"
                  />
                </div>

                <div className="employee-form-field">
                  <label htmlFor="postal_code" className="employee-field-label">
                    Code postal *
                  </label>
                  <input
                    id="postal_code"
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    className={`employee-input ${errors.postal_code ? 'employee-input-error' : ''}`}
                    placeholder="75001"
                    maxLength="5"
                  />
                  {errors.postal_code && (
                    <span className="employee-field-error">{errors.postal_code}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="city" className="employee-field-label">
                    Ville *
                  </label>
                  <input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className={`employee-input ${errors.city ? 'employee-input-error' : ''}`}
                    placeholder="Paris"
                  />
                  {errors.city && (
                    <span className="employee-field-error">{errors.city}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="country" className="employee-field-label">
                    Pays (code ISO)
                  </label>
                  <input
                    id="country"
                    type="text"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value.toUpperCase())}
                    className="employee-input"
                    placeholder="FR"
                    maxLength="2"
                  />
                  <div className="employee-field-help">
                    Code pays sur 2 lettres (ex: FR, BE, CH)
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2b: Ancienneté & Lieu de travail */}
            <section className="employee-section">
              <h3 className="employee-section-title">Ancienneté & Lieu de travail</h3>
              <div className="employee-form-grid">
                <div className="employee-form-field">
                  <label htmlFor="seniority_date" className="employee-field-label">
                    Date d'ancienneté
                  </label>
                  <input
                    id="seniority_date"
                    type="date"
                    value={formData.seniority_date}
                    onChange={(e) => handleChange('seniority_date', e.target.value)}
                    className={`employee-input ${errors.seniority_date ? 'employee-input-error' : ''}`}
                  />
                  <span className="employee-subtle-hint">Si reprise d'ancienneté (antérieure à la date d'entrée)</span>
                  {errors.seniority_date && (
                    <span className="employee-field-error">{errors.seniority_date}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="workplace_location" className="employee-field-label">
                    Lieu habituel de travail
                  </label>
                  <input
                    id="workplace_location"
                    type="text"
                    value={formData.workplace_location}
                    onChange={(e) => handleChange('workplace_location', e.target.value)}
                    className="employee-input"
                    placeholder="ex: Établissement de Paris"
                  />
                  <span className="employee-subtle-hint">Ville ou nom de l'établissement</span>
                </div>
              </div>
            </section>

            {/* Section 3: Contrat & Rémunération */}
            <section className="employee-section">
              <h3 className="employee-section-title">Contrat & Rémunération</h3>
              <div className="employee-form-grid">
                <div className="employee-form-field">
                  <label htmlFor="contract_type" className="employee-field-label">
                    Type de contrat *
                  </label>
                  <select
                    id="contract_type"
                    value={formData.contract_type}
                    onChange={(e) => handleChange('contract_type', e.target.value)}
                    className="employee-input"
                  >
                    {contractTypes.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="employee-form-field">
                  <label htmlFor="start_date" className="employee-field-label">
                    Date d'entrée *
                  </label>
                  <input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    className={`employee-input ${errors.start_date ? 'employee-input-error' : ''}`}
                  />
                  {errors.start_date && (
                    <span className="employee-field-error">{errors.start_date}</span>
                  )}
                </div>

                {(formData.contract_type === 'CDD' || formData.contract_type === 'APPRENTI') && (
                  <div className="employee-form-field">
                    <label htmlFor="end_date" className="employee-field-label">
                      Date de fin{formData.contract_type === 'APPRENTI' ? ' *' : ''}
                    </label>
                    <input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => handleChange('end_date', e.target.value)}
                      className="employee-input"
                    />
                    {formData.contract_type === 'APPRENTI' && (
                      <div className="employee-field-help">
                        Obligatoire pour les apprentis (calcul de l'année d'apprentissage)
                      </div>
                    )}
                  </div>
                )}

                <div className="employee-form-field">
                  <label htmlFor="working_time_pct" className="employee-field-label">
                    Temps de travail (%)
                  </label>
                  <input
                    id="working_time_pct"
                    type="number"
                    value={formData.working_time_pct}
                    onChange={(e) => handleChange('working_time_pct', e.target.value)}
                    className="employee-input"
                    min="1"
                    max="100"
                  />
                </div>

                <div className="employee-form-field">
                  <label htmlFor="weekly_hours" className="employee-field-label">
                    Heures/semaine *
                  </label>
                  <input
                    id="weekly_hours"
                    type="number"
                    value={formData.weekly_hours}
                    onChange={(e) => handleChange('weekly_hours', e.target.value)}
                    className="employee-input"
                    min="1"
                    max="60"
                  />
                </div>

                <div className="employee-form-field">
                  <label htmlFor="salary_type" className="employee-field-label">
                    Type de salaire
                  </label>
                  <select
                    id="salary_type"
                    value={formData.salary_type}
                    onChange={(e) => handleChange('salary_type', e.target.value)}
                    className="employee-input"
                  >
                    {salaryTypes.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="employee-form-field">
                  <label htmlFor="salary_gross_base" className="employee-field-label">
                    Salaire brut de base *
                  </label>
                  <input
                    id="salary_gross_base"
                    type="number"
                    value={formData.salary_gross_base}
                    onChange={(e) => handleChange('salary_gross_base', e.target.value)}
                    className={`employee-input ${errors.salary_gross_base ? 'employee-input-error' : ''}`}
                    placeholder="2500"
                    min="0"
                    step="0.01"
                  />
                  {errors.salary_gross_base && (
                    <span className="employee-field-error">{errors.salary_gross_base}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="withholding_code" className="employee-field-label">
                    Code PAS
                  </label>
                  <input
                    id="withholding_code"
                    type="text"
                    value={formData.withholding_code || '0'}
                    onChange={(e) => handleChange('withholding_code', e.target.value)}
                    className="employee-input"
                    placeholder="0"
                  />
                  <div className="employee-field-help">
                    Code de prélèvement à la source (0 = non personnalisé). Trouvez-le sur impots.gouv.fr dans l'espace professionnel.
                  </div>
                </div>

                <div className="employee-form-field">
                  <label htmlFor="withholding_rate" className="employee-field-label">
                    Taux PAS
                  </label>
                  <input
                    id="withholding_rate"
                    type="number"
                    value={formData.withholding_rate || '0'}
                    onChange={(e) => handleChange('withholding_rate', e.target.value)}
                    className={`employee-input ${errors.withholding_rate ? 'employee-input-error' : ''}`}
                    placeholder="0"
                    min="0"
                    max="1"
                    step="0.01"
                  />
                  <div className="employee-field-help">
                    Taux de prélèvement (ex: 0.10 pour 10%). Disponible sur la déclaration PASRAU ou impots.gouv.fr.
                  </div>
                  {errors.withholding_rate && (
                    <span className="employee-field-error">{errors.withholding_rate}</span>
                  )}
                </div>
              </div>
            </section>

            {/* Section 3b: Classification & Convention */}
            <section className="employee-section">
              <h3 className="employee-section-title">Classification & Convention</h3>
              {cabinet?.convention_idcc === '1619' && (!formData.job_title || !formData.classification_coefficient) && (
                <div className="employee-alert-warning" style={{ marginBottom: '16px' }}>
                  <div className="employee-alert-icon">⚠️</div>
                  <div className="employee-alert-content">
                    <strong>IDCC 1619 (cabinets dentaires) :</strong> Intitulé du poste et coefficient sont fortement recommandés pour une paie conforme.
                  </div>
                </div>
              )}
              
              {/* Rendu conditionnel selon IDCC */}
              {cabinet?.convention_idcc === '1619' ? (
                <div className="employee-form-grid">
                  {/* Sélecteur des métiers 1619 */}
                  <div className="employee-form-field employee-form-field-full">
                    <label className="employee-field-label">Métier (IDCC 1619)</label>
                    <select
                      value={formData.job_title_select}
                      onChange={(e) => handleChange('job_title_select', e.target.value)}
                      className="employee-input"
                    >
                      <option value="">Sélectionner…</option>
                      {DENTAL_1619_ROLES.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <span className="employee-subtle-hint">Choisissez un métier de la convention 1619 ou "Autre…" pour saisir manuellement</span>
                  </div>

                  {/* Quand ce n'est PAS "Autre…" et qu'un métier est sélectionné, champs en lecture seule */}
                  {formData.job_title_select && formData.job_title_select !== 'autre' ? (
                    <>
                      <div className="employee-form-field">
                        <label className="employee-field-label">Intitulé du poste</label>
                        <input className="employee-input" value={formData.job_title || ''} readOnly style={{ opacity: 0.85, pointerEvents: 'none' }} />
                      </div>

                      <div className="employee-form-field">
                        <label className="employee-field-label">Niveau de classification</label>
                        <input className="employee-input" value={formData.classification_level || ''} readOnly style={{ opacity: 0.85, pointerEvents: 'none' }} />
                      </div>

                      <div className="employee-form-field">
                        <label className="employee-field-label">Échelon</label>
                        <input className="employee-input" value={formData.classification_echelon || ''} readOnly style={{ opacity: 0.85, pointerEvents: 'none' }} />
                      </div>

                      <div className="employee-form-field">
                        <label className="employee-field-label">Coefficient</label>
                        <input className="employee-input" value={formData.classification_coefficient || ''} readOnly style={{ opacity: 0.85, pointerEvents: 'none' }} />
                      </div>

                      <div className="employee-form-field">
                        <label htmlFor="position_code" className="employee-field-label">
                          Code poste interne
                        </label>
                        <input
                          id="position_code"
                          type="text"
                          value={formData.position_code}
                          onChange={(e) => handleChange('position_code', e.target.value)}
                          className="employee-input"
                          placeholder="ex: AD-01"
                        />
                      </div>

                      <div className="employee-form-field">
                        <label htmlFor="status_cadre" className="employee-field-label">Statut cadre</label>
                        <select
                          id="status_cadre"
                          value={formData.status_cadre}
                          onChange={(e) => handleChange('status_cadre', e.target.value)}
                          className="employee-input"
                        >
                          {statusCadreOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        {formData.status_cadre === 'CADRE' && (
                          <span className="employee-subtle-hint" style={{ color: '#3b82f6' }}>
                            Le statut cadre impacte les cotisations retraite Agirc-Arrco et les tranches de rémunération
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    // "Autre…" ou rien sélectionné : saisie libre (comme avant)
                    <>
                      <div className="employee-form-field">
                        <label htmlFor="job_title" className="employee-field-label">Intitulé du poste</label>
                        <input
                          id="job_title"
                          type="text"
                          value={formData.job_title}
                          onChange={(e) => handleChange('job_title', e.target.value)}
                          className="employee-input"
                          placeholder="ex: Assistante dentaire qualifiée"
                        />
                        <span className="employee-subtle-hint">Fonction ou emploi occupé</span>
                      </div>

                      <div className="employee-form-field">
                        <label htmlFor="position_code" className="employee-field-label">
                          Code poste interne
                        </label>
                        <input
                          id="position_code"
                          type="text"
                          value={formData.position_code}
                          onChange={(e) => handleChange('position_code', e.target.value)}
                          className="employee-input"
                          placeholder="ex: AD-01"
                        />
                      </div>

                      <div className="employee-form-field">
                        <label htmlFor="classification_level" className="employee-field-label">Niveau de classification</label>
                        <input
                          id="classification_level"
                          type="text"
                          value={formData.classification_level}
                          onChange={(e) => handleChange('classification_level', e.target.value)}
                          className="employee-input"
                          placeholder="ex: Niveau II"
                        />
                      </div>

                      <div className="employee-form-field">
                        <label htmlFor="classification_echelon" className="employee-field-label">Échelon</label>
                        <input
                          id="classification_echelon"
                          type="text"
                          value={formData.classification_echelon}
                          onChange={(e) => handleChange('classification_echelon', e.target.value)}
                          className="employee-input"
                          placeholder="ex: Échelon 3"
                        />
                      </div>

                      <div className="employee-form-field">
                        <label htmlFor="classification_coefficient" className="employee-field-label">Coefficient</label>
                        <input
                          id="classification_coefficient"
                          type="number"
                          step="0.01"
                          value={formData.classification_coefficient}
                          onChange={(e) => handleChange('classification_coefficient', e.target.value)}
                          className={`employee-input ${errors.classification_coefficient ? 'employee-input-error' : ''}`}
                          placeholder="ex: 240"
                        />
                        {errors.classification_coefficient && (
                          <span className="employee-field-error">{errors.classification_coefficient}</span>
                        )}
                      </div>

                      <div className="employee-form-field">
                        <label htmlFor="status_cadre" className="employee-field-label">Statut cadre</label>
                        <select
                          id="status_cadre"
                          value={formData.status_cadre}
                          onChange={(e) => handleChange('status_cadre', e.target.value)}
                          className="employee-input"
                        >
                          {statusCadreOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        {formData.status_cadre === 'CADRE' && (
                          <span className="employee-subtle-hint" style={{ color: '#3b82f6' }}>
                            Le statut cadre impacte les cotisations retraite Agirc-Arrco et les tranches de rémunération
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* Cas non-1619 : conserve le rendu existant inchangé */
                <div className="employee-form-grid">
                  <div className="employee-form-field">
                    <label htmlFor="job_title" className="employee-field-label">
                      Intitulé du poste
                    </label>
                    <input
                      id="job_title"
                      type="text"
                      value={formData.job_title}
                      onChange={(e) => handleChange('job_title', e.target.value)}
                      className="employee-input"
                      placeholder="ex: Assistante dentaire qualifiée"
                    />
                    <span className="employee-subtle-hint">Fonction ou emploi occupé</span>
                  </div>

                  <div className="employee-form-field">
                    <label htmlFor="position_code" className="employee-field-label">
                      Code poste interne
                    </label>
                    <input
                      id="position_code"
                      type="text"
                      value={formData.position_code}
                      onChange={(e) => handleChange('position_code', e.target.value)}
                      className="employee-input"
                      placeholder="ex: AD-01"
                    />
                  </div>

                  <div className="employee-form-field">
                    <label htmlFor="classification_level" className="employee-field-label">
                      Niveau de classification
                    </label>
                    <input
                      id="classification_level"
                      type="text"
                      value={formData.classification_level}
                      onChange={(e) => handleChange('classification_level', e.target.value)}
                      className="employee-input"
                      placeholder="ex: Niveau II"
                    />
                  </div>

                  <div className="employee-form-field">
                    <label htmlFor="classification_echelon" className="employee-field-label">
                      Échelon
                    </label>
                    <input
                      id="classification_echelon"
                      type="text"
                      value={formData.classification_echelon}
                      onChange={(e) => handleChange('classification_echelon', e.target.value)}
                      className="employee-input"
                      placeholder="ex: Échelon 3"
                    />
                  </div>

                  <div className="employee-form-field">
                    <label htmlFor="classification_coefficient" className="employee-field-label">
                      Coefficient
                    </label>
                    <input
                      id="classification_coefficient"
                      type="number"
                      step="0.01"
                      value={formData.classification_coefficient}
                      onChange={(e) => handleChange('classification_coefficient', e.target.value)}
                      className={`employee-input ${errors.classification_coefficient ? 'employee-input-error' : ''}`}
                      placeholder="ex: 240"
                    />
                    {errors.classification_coefficient && (
                      <span className="employee-field-error">{errors.classification_coefficient}</span>
                    )}
                  </div>

                  <div className="employee-form-field">
                    <label htmlFor="status_cadre" className="employee-field-label">
                      Statut cadre
                    </label>
                    <select
                      id="status_cadre"
                      value={formData.status_cadre}
                      onChange={(e) => handleChange('status_cadre', e.target.value)}
                      className="employee-input"
                    >
                      {statusCadreOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {formData.status_cadre === 'CADRE' && (
                      <span className="employee-subtle-hint" style={{ color: '#3b82f6' }}>
                        Le statut cadre impacte les cotisations retraite Agirc-Arrco et les tranches de rémunération
                      </span>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Section 4: École (pour apprentis uniquement) */}
            {formData.contract_type === 'APPRENTI' && (
              <section className="employee-section">
                <h3 className="employee-section-title">École</h3>
                <div className="employee-form-grid">
                  <div className="employee-form-field">
                    <label htmlFor="school_hours_per_week" className="employee-field-label">
                      Heures école/semaine *
                    </label>
                    <input
                      id="school_hours_per_week"
                      type="number"
                      value={formData.school_hours_per_week}
                      onChange={(e) => handleChange('school_hours_per_week', e.target.value)}
                      className={`employee-input ${errors.school_hours_per_week ? 'employee-input-error' : ''}`}
                      min="1"
                      max="40"
                      placeholder="7"
                    />
                    {errors.school_hours_per_week && (
                      <span className="employee-field-error">{errors.school_hours_per_week}</span>
                    )}
                    {!errors.school_hours_per_week && (
                      <div className="employee-field-help">
                        Nombre d'heures de cours par semaine
                      </div>
                    )}
                  </div>

                  <div className="employee-form-field employee-form-field-full">
                    <label className="employee-field-label">
                      Jours d'école * (sélectionnez un ou plusieurs jours)
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                      {schoolDayOptions.map(option => (
                        <label key={option.value} className="employee-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.school_days.includes(option.value)}
                            onChange={(e) => {
                              const newDays = e.target.checked
                                ? [...formData.school_days, option.value]
                                : formData.school_days.filter(day => day !== option.value);
                              handleChange('school_days', newDays);
                            }}
                            className="employee-checkbox"
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                    {errors.school_days && (
                      <span className="employee-field-error">{errors.school_days}</span>
                    )}
                    {!errors.school_days && (
                      <div className="employee-field-help">
                        Sélectionnez tous les jours où l'apprenti est à l'école
                      </div>
                    )}
                  </div>

                  {/* Nouveaux champs école (optionnels) */}
                  <div className="employee-form-field">
                    <label htmlFor="school_name" className="employee-field-label">
                      Nom de l'école
                    </label>
                    <input
                      id="school_name"
                      type="text"
                      value={formData.school_name}
                      onChange={(e) => handleChange('school_name', e.target.value)}
                      className="employee-input"
                      placeholder="ex: CFA Médical de Paris"
                    />
                    <span className="employee-subtle-hint">Optionnel (non requis pour la paie)</span>
                  </div>

                  <div className="employee-form-field">
                    <label htmlFor="school_city" className="employee-field-label">
                      Ville de l'école
                    </label>
                    <input
                      id="school_city"
                      type="text"
                      value={formData.school_city}
                      onChange={(e) => handleChange('school_city', e.target.value)}
                      className="employee-input"
                      placeholder="ex: Paris"
                    />
                    <span className="employee-subtle-hint">Optionnel (non requis pour la paie)</span>
                  </div>
                </div>
              </section>
            )}

            {/* Section 5: Organismes */}
            <section className="employee-section">
              <h3 className="employee-section-title">Organismes sociaux</h3>
              <div className="employee-form-grid">
                <div className="employee-form-field">
                  <label htmlFor="mutuelle_status" className="employee-field-label">
                    Mutuelle
                  </label>
                  <select
                    id="mutuelle_status"
                    value={formData.mutuelle_status}
                    onChange={(e) => handleChange('mutuelle_status', e.target.value)}
                    className="employee-input"
                  >
                    {mutuelleStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.mutuelle_status === 'with_mutuelle' && (
                  <div className="employee-form-field">
                    <label htmlFor="mutuelle_selected" className="employee-field-label">
                      Sélectionner une mutuelle
                    </label>
                    <select
                      id="mutuelle_selected"
                      value={formData.mutuelle_selected}
                      onChange={(e) => handleChange('mutuelle_selected', e.target.value)}
                      className="employee-input"
                    >
                      {mutuelleOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.mutuelle_status === 'no_mutuelle' && (
                  <div className="employee-form-field">
                    <label htmlFor="mutuelle_opt_out_reason" className="employee-field-label">
                      Motif de dispense
                    </label>
                    <input
                      id="mutuelle_opt_out_reason"
                      type="text"
                      value={formData.mutuelle_opt_out_reason}
                      onChange={(e) => handleChange('mutuelle_opt_out_reason', e.target.value)}
                      className="employee-input"
                      placeholder="Motif de la dispense"
                    />
                  </div>
                )}

                <div className="employee-form-field">
                  <label htmlFor="prevoyance_selected" className="employee-field-label">
                    Prévoyance
                  </label>
                  <select
                    id="prevoyance_selected"
                    value={formData.prevoyance_selected}
                    onChange={(e) => handleChange('prevoyance_selected', e.target.value)}
                    className="employee-input"
                  >
                    {prevoyanceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="employee-form-field">
                  <label htmlFor="retraite_institution" className="employee-field-label">
                    Retraite complémentaire
                  </label>
                  <select
                    id="retraite_institution"
                    value={formData.retraite_institution}
                    onChange={(e) => handleChange('retraite_institution', e.target.value)}
                    className="employee-input"
                  >
                    {retraiteOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="employee-organismes-help">
                <strong>Note :</strong> Pour l'IDCC 1619 (cabinets dentaires), mutuelle et prévoyance sont généralement obligatoires. 
                Vous pouvez enregistrer sans, mais la conformité n'est pas garantie.
              </div>
            </section>

            {/* Section 5b: Titres-restaurant & Transport */}
            <section className="employee-section">
              <h3 className="employee-section-title">Avantages & Remboursements</h3>
              
              {/* Titres-restaurant */}
              <div style={{ marginBottom: '20px' }}>
                <div className="employee-switch" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <input
                    type="checkbox"
                    id="meal_vouchers_enabled"
                    checked={formData.meal_vouchers_enabled}
                    onChange={(e) => handleChange('meal_vouchers_enabled', e.target.checked)}
                    className="employee-checkbox"
                  />
                  <label htmlFor="meal_vouchers_enabled" className="employee-field-label" style={{ margin: 0, cursor: 'pointer' }}>
                    Titres-restaurant
                  </label>
                </div>

                {formData.meal_vouchers_enabled && (
                  <div className="employee-form-grid">
                    <div className="employee-form-field">
                      <label htmlFor="meal_voucher_value" className="employee-field-label">
                        Valeur faciale (€) *
                      </label>
                      <input
                        id="meal_voucher_value"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.meal_voucher_value}
                        onChange={(e) => handleChange('meal_voucher_value', e.target.value)}
                        className={`employee-input ${errors.meal_voucher_value ? 'employee-input-error' : ''}`}
                        placeholder="ex: 9.50"
                      />
                      <span className="employee-subtle-hint">Montant total du titre-restaurant</span>
                      {errors.meal_voucher_value && (
                        <span className="employee-field-error">{errors.meal_voucher_value}</span>
                      )}
                    </div>

                    <div className="employee-form-field">
                      <label htmlFor="meal_voucher_employer_share_percent" className="employee-field-label">
                        Part employeur (%) *
                      </label>
                      <input
                        id="meal_voucher_employer_share_percent"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={formData.meal_voucher_employer_share_percent}
                        onChange={(e) => handleChange('meal_voucher_employer_share_percent', e.target.value)}
                        className={`employee-input ${errors.meal_voucher_employer_share_percent ? 'employee-input-error' : ''}`}
                        placeholder="ex: 60"
                      />
                      <span className="employee-subtle-hint">Pourcentage pris en charge par l'employeur</span>
                      {errors.meal_voucher_employer_share_percent && (
                        <span className="employee-field-error">{errors.meal_voucher_employer_share_percent}</span>
                      )}
                    </div>

                    <div className="employee-form-field">
                      <label htmlFor="meal_voucher_days_per_month" className="employee-field-label">
                        Jours/mois *
                      </label>
                      <input
                        id="meal_voucher_days_per_month"
                        type="number"
                        min="0"
                        max="31"
                        value={formData.meal_voucher_days_per_month}
                        onChange={(e) => handleChange('meal_voucher_days_per_month', e.target.value)}
                        className={`employee-input ${errors.meal_voucher_days_per_month ? 'employee-input-error' : ''}`}
                        placeholder="ex: 18"
                      />
                      <span className="employee-subtle-hint">Nombre de jours travaillés par mois</span>
                      {errors.meal_voucher_days_per_month && (
                        <span className="employee-field-error">{errors.meal_voucher_days_per_month}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Transport */}
              <div>
                <div className="employee-switch" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <input
                    type="checkbox"
                    id="transport_enabled"
                    checked={formData.transport_enabled}
                    onChange={(e) => handleChange('transport_enabled', e.target.checked)}
                    className="employee-checkbox"
                  />
                  <label htmlFor="transport_enabled" className="employee-field-label" style={{ margin: 0, cursor: 'pointer' }}>
                    Remboursement transport (ex: Navigo)
                  </label>
                </div>

                {formData.transport_enabled && (
                  <div className="employee-form-grid">
                    <div className="employee-form-field">
                      <label htmlFor="transport_monthly_cost" className="employee-field-label">
                        Coût mensuel (€) *
                      </label>
                      <input
                        id="transport_monthly_cost"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.transport_monthly_cost}
                        onChange={(e) => handleChange('transport_monthly_cost', e.target.value)}
                        className={`employee-input ${errors.transport_monthly_cost ? 'employee-input-error' : ''}`}
                        placeholder="ex: 86.30"
                      />
                      <span className="employee-subtle-hint">Montant total de l'abonnement</span>
                      {errors.transport_monthly_cost && (
                        <span className="employee-field-error">{errors.transport_monthly_cost}</span>
                      )}
                    </div>

                    <div className="employee-form-field">
                      <label htmlFor="transport_employer_share_percent" className="employee-field-label">
                        Part employeur (%) *
                      </label>
                      <input
                        id="transport_employer_share_percent"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={formData.transport_employer_share_percent}
                        onChange={(e) => handleChange('transport_employer_share_percent', e.target.value)}
                        className={`employee-input ${errors.transport_employer_share_percent ? 'employee-input-error' : ''}`}
                        placeholder="ex: 50"
                      />
                      <span className="employee-subtle-hint">Pourcentage pris en charge (min. 50%)</span>
                      {errors.transport_employer_share_percent && (
                        <span className="employee-field-error">{errors.transport_employer_share_percent}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Section 5c: Avantages en nature */}
            <section className="employee-section">
              <h3 className="employee-section-title">Avantages en nature</h3>
              <div className="employee-form-grid">
                <div className="employee-form-field">
                  <label htmlFor="benefit_vehicle_amount" className="employee-field-label">
                    Véhicule (€/mois)
                  </label>
                  <input
                    id="benefit_vehicle_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.benefit_vehicle_amount}
                    onChange={(e) => handleChange('benefit_vehicle_amount', e.target.value)}
                    className={`employee-input ${errors.benefit_vehicle_amount ? 'employee-input-error' : ''}`}
                    placeholder="0.00"
                  />
                  <span className="employee-subtle-hint">Évaluation forfaitaire mensuelle</span>
                  {errors.benefit_vehicle_amount && (
                    <span className="employee-field-error">{errors.benefit_vehicle_amount}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="benefit_housing_amount" className="employee-field-label">
                    Logement (€/mois)
                  </label>
                  <input
                    id="benefit_housing_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.benefit_housing_amount}
                    onChange={(e) => handleChange('benefit_housing_amount', e.target.value)}
                    className={`employee-input ${errors.benefit_housing_amount ? 'employee-input-error' : ''}`}
                    placeholder="0.00"
                  />
                  <span className="employee-subtle-hint">Évaluation forfaitaire mensuelle</span>
                  {errors.benefit_housing_amount && (
                    <span className="employee-field-error">{errors.benefit_housing_amount}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="benefit_meals_amount" className="employee-field-label">
                    Repas (€/mois)
                  </label>
                  <input
                    id="benefit_meals_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.benefit_meals_amount}
                    onChange={(e) => handleChange('benefit_meals_amount', e.target.value)}
                    className={`employee-input ${errors.benefit_meals_amount ? 'employee-input-error' : ''}`}
                    placeholder="0.00"
                  />
                  <span className="employee-subtle-hint">Évaluation forfaitaire mensuelle</span>
                  {errors.benefit_meals_amount && (
                    <span className="employee-field-error">{errors.benefit_meals_amount}</span>
                  )}
                </div>
              </div>
            </section>

            {/* Section 6: Paiement */}
            <section className="employee-section">
              <h3 className="employee-section-title">Paiement</h3>
              <div className="employee-form-grid">
                <div className="employee-form-field">
                  <label htmlFor="employee_iban" className="employee-field-label">
                    IBAN
                  </label>
                  <input
                    id="employee_iban"
                    type="text"
                    value={formData.employee_iban}
                    onChange={(e) => handleChange('employee_iban', e.target.value)}
                    className={`employee-input ${errors.employee_iban ? 'employee-input-error' : ''}`}
                    placeholder="FR76 3000 6000 0112 3456 7890 189"
                  />
                  {errors.employee_iban && (
                    <span className="employee-field-error">{errors.employee_iban}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="employee_bic" className="employee-field-label">
                    BIC
                  </label>
                  <input
                    id="employee_bic"
                    type="text"
                    value={formData.employee_bic}
                    onChange={(e) => handleChange('employee_bic', e.target.value)}
                    className={`employee-input ${errors.employee_bic ? 'employee-input-error' : ''}`}
                    placeholder="AGRIFRPP"
                  />
                  {errors.employee_bic && (
                    <span className="employee-field-error">{errors.employee_bic}</span>
                  )}
                </div>

                <div className="employee-form-field">
                  <label htmlFor="payment_method" className="employee-field-label">
                    Méthode de paiement
                  </label>
                  <select
                    id="payment_method"
                    value={formData.payment_method}
                    onChange={(e) => handleChange('payment_method', e.target.value)}
                    className="employee-input"
                  >
                    {paymentMethods.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            {/* Section 7: Notes */}
            <section className="employee-section">
              <h3 className="employee-section-title">Notes</h3>
              <div className="employee-form-field employee-form-field-full">
                <label htmlFor="note" className="employee-field-label">
                  Note interne
                </label>
                <textarea
                  id="note"
                  value={formData.note}
                  onChange={(e) => handleChange('note', e.target.value)}
                  className="employee-textarea"
                  rows="3"
                  placeholder="Informations complémentaires..."
                />
              </div>
            </section>

          </div>

          <div className="employee-modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="employee-btn employee-btn-cancel"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="employee-btn employee-btn-primary"
              disabled={loading}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
